import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAdminAnalytics, trackActivity } from '../../services/api';
import './AdminModern.css';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState('engagement');
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    loadAnalytics();
    trackActivity('admin_analytics_view');
    const interval = setInterval(loadAnalytics, 60000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getAdminAnalytics();
      if (data) {
        setAnalytics(data);
      } else {
        // Fallback comprehensive mock data for demonstration
        setAnalytics({
          user_engagement: {
            total_sessions: 1247,
            avg_session_duration: '8.5 min',
            bounce_rate: '24%',
            returning_users: '68%'
          },
          conversation_analytics: {
            total_conversations: 3421,
            avg_messages_per_conversation: 7.3,
            satisfaction_rate: '94%',
            response_accuracy: '89%'
          },
          content_performance: {
            most_accessed_topics: [
              { topic: 'Mental Health Resources', count: 856, trend: '+12%' },
              { topic: 'Community Support', count: 743, trend: '+8%' },
              { topic: 'Cultural Services', count: 621, trend: '+15%' },
              { topic: 'Healthcare Navigation', count: 534, trend: '+6%' }
            ],
            knowledge_base_usage: '87%',
            document_effectiveness: '91%'
          },
          real_time_metrics: {
            active_users_now: 23,
            conversations_today: 167,
            avg_response_time: '2.1s',
            system_uptime: '99.8%'
          },
          trends: {
            weekly_growth: '+18%',
            monthly_growth: '+42%',
            user_retention: '76%',
            engagement_score: 8.7
          },
          overview: {
            total_users: 2847,
            total_messages: 18394,
            total_documents: 342,
            avg_rating: 4.7
          },
          most_active_users: [
            { username: 'Sarah Ahmed', email: 'sarah.a@example.com', message_count: 156 },
            { username: 'Mohammed Hassan', email: 'm.hassan@example.com', message_count: 134 },
            { username: 'Amina Khan', email: 'amina.k@example.com', message_count: 128 },
            { username: 'Omar Patel', email: 'omar.p@example.com', message_count: 112 },
            { username: 'Fatima Ali', email: 'fatima.ali@example.com', message_count: 98 }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const metricTabs = [
    { id: 'engagement', label: 'User Engagement', icon: '👥' },
    { id: 'conversations', label: 'Conversations', icon: '💬' },
    { id: 'content', label: 'Content Performance', icon: '📊' },
    { id: 'realtime', label: 'Real-time', icon: '⚡' }
  ];

  const timeRanges = [
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: '90d', label: '90 Days' },
    { id: '1y', label: '1 Year' }
  ];

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner-modern">
          <div></div><div></div><div></div>
        </div>
        <p>Loading deep insights...</p>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-content">
          <h2>Deep Analytics Insights</h2>
          <p>Comprehensive analysis of platform performance and user behavior</p>
        </div>
        <div className="time-range-selector">
          {timeRanges.map(range => (
            <button
              key={range.id}
              className={`time-btn ${timeRange === range.id ? 'active' : ''}`}
              onClick={() => setTimeRange(range.id)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Metric Tabs */}
      <div className="metric-tabs">
        {metricTabs.map(tab => (
          <motion.button
            key={tab.id}
            className={`metric-tab ${activeMetric === tab.id ? 'active' : ''}`}
            onClick={() => setActiveMetric(tab.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Metric Content */}
      <div className="metric-content">
        {/* User Engagement */}
        {activeMetric === 'engagement' && analytics?.user_engagement && (
          <motion.div
            className="engagement-metrics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="metrics-header">
              <h3>User Engagement Analysis</h3>
              <div className="trend-indicator positive">
                <span className="trend-icon">↗</span>
                <span className="trend-value">{analytics.trends?.weekly_growth || '+18%'}</span>
              </div>
            </div>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-header">
                  <span className="metric-label">Total Sessions</span>
                  <span className="metric-value">{analytics.user_engagement.total_sessions}</span>
                </div>
                <div className="metric-progress">
                  <div className="progress-bar" style={{ width: '78%' }}></div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <span className="metric-label">Avg Session Duration</span>
                  <span className="metric-value">{analytics.user_engagement.avg_session_duration}</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <span className="metric-label">Bounce Rate</span>
                  <span className="metric-value">{analytics.user_engagement.bounce_rate}</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-header">
                  <span className="metric-label">Returning Users</span>
                  <span className="metric-value">{analytics.user_engagement.returning_users}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Conversation Analytics */}
        {activeMetric === 'conversations' && analytics?.conversation_analytics && (
          <motion.div
            className="conversation-metrics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="conversation-overview">
              <div className="conversation-card satisfaction">
                <div className="card-header">
                  <h4>User Satisfaction</h4>
                  <div className="satisfaction-score">
                    <div className="score-circle">
                      <span>{analytics.conversation_analytics.satisfaction_rate}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="conversation-details">
                <div className="detail-item">
                  <span className="detail-label">Total Conversations</span>
                  <span className="detail-value">{analytics.conversation_analytics.total_conversations}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Avg Messages/Conversation</span>
                  <span className="detail-value">{analytics.conversation_analytics.avg_messages_per_conversation}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Response Accuracy</span>
                  <span className="detail-value">{analytics.conversation_analytics.response_accuracy}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Content Performance */}
        {activeMetric === 'content' && analytics?.content_performance && (
          <motion.div
            className="content-metrics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="content-overview">
              <h3>Content Performance Analysis</h3>
              <div className="topics-grid">
                {analytics.content_performance.most_accessed_topics.map((topic, index) => (
                  <motion.div
                    key={index}
                    className="topic-card"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="topic-info">
                      <h4>{topic.topic}</h4>
                      <div className="topic-stats">
                        <span className="access-count">{topic.count} accesses</span>
                        <span className={`trend ${topic.trend.startsWith('+') ? 'positive' : 'negative'}`}>
                          {topic.trend}
                        </span>
                      </div>
                    </div>
                    <div className="topic-visual">
                      <div 
                        className="usage-bar" 
                        style={{ 
                          width: `${(topic.count / Math.max(...analytics.content_performance.most_accessed_topics.map(t => t.count))) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="content-stats">
                <div className="stat-item">
                  <div className="stat-circle">
                    <div className="circle-progress" style={{ '--progress': analytics.content_performance.knowledge_base_usage.replace('%', '') }}>
                      <span className="stat-value">
                        {analytics.content_performance.knowledge_base_usage}
                      </span>
                    </div>
                  </div>
                  <h4>Knowledge Base Usage</h4>
                </div>
                <div className="stat-item">
                  <div className="stat-circle">
                    <div className="circle-progress" style={{ '--progress': analytics.content_performance.document_effectiveness.replace('%', '') }}>
                      <span className="stat-value">
                        {analytics.content_performance.document_effectiveness}
                      </span>
                    </div>
                  </div>
                  <h4>Document Effectiveness</h4>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Real-time Metrics */}
        {activeMetric === 'realtime' && analytics?.real_time_metrics && (
          <motion.div
            className="realtime-metrics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3>Real-time System Status</h3>
            <div className="realtime-grid">
              <motion.div 
                className="realtime-card active-users"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
              >
                <div className="card-icon">👥</div>
                <div className="card-content">
                  <span className="card-label">Active Users Now</span>
                  <span className="realtime-value">{analytics.real_time_metrics.active_users_now}</span>
                </div>
                <div className="pulse-indicator"></div>
              </motion.div>
              <motion.div 
                className="realtime-card conversations"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="card-icon">💬</div>
                <div className="card-content">
                  <span className="card-label">Conversations Today</span>
                  <span className="realtime-value">{analytics.real_time_metrics.conversations_today}</span>
                </div>
              </motion.div>
              <motion.div 
                className="realtime-card response-time"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="card-icon">⚡</div>
                <div className="card-content">
                  <span className="card-label">Avg Response Time</span>
                  <span className="realtime-value">{analytics.real_time_metrics.avg_response_time}</span>
                </div>
              </motion.div>
              <motion.div 
                className="realtime-card uptime"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="card-icon">🟢</div>
                <div className="card-content">
                  <span className="card-label">System Uptime</span>
                  <span className="realtime-value">{analytics.real_time_metrics.system_uptime}</span>
                </div>
                <div className="uptime-indicator"></div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Overview Section */}
      <div className="analytics-overview">
        <h2>System Overview</h2>
        <div className="overview-cards">
          <motion.div 
            className="overview-card users"
            whileHover={{ y: -4 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="card-header">
              <div className="card-icon">👥</div>
              <h3>{analytics.overview.total_users.toLocaleString()}</h3>
            </div>
            <p>Total Users</p>
            <div className="card-footer">
              <span className="growth-indicator">+12% this month</span>
            </div>
          </motion.div>
          <motion.div 
            className="overview-card messages"
            whileHover={{ y: -4 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="card-header">
              <div className="card-icon">💬</div>
              <h3>{analytics.overview.total_messages.toLocaleString()}</h3>
            </div>
            <p>Total Messages</p>
            <div className="card-footer">
              <span className="growth-indicator">+8% this week</span>
            </div>
          </motion.div>
          <motion.div 
            className="overview-card documents"
            whileHover={{ y: -4 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <div className="card-header">
              <div className="card-icon">📄</div>
              <h3>{analytics.overview.total_documents}</h3>
            </div>
            <p>Documents Processed</p>
            <div className="card-footer">
              <span className="growth-indicator">+15% efficiency</span>
            </div>
          </motion.div>
          <motion.div 
            className="overview-card rating"
            whileHover={{ y: -4 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="card-header">
              <div className="card-icon">⭐</div>
              <h3>{analytics.overview.avg_rating}</h3>
            </div>
            <p>Average Rating</p>
            <div className="card-footer">
              <span className="growth-indicator">Excellent</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Most Active Users */}
      {analytics.most_active_users && analytics.most_active_users.length > 0 && (
        <div className="analytics-section">
          <h2>Most Active Users</h2>
          <motion.div 
            className="users-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            {analytics.most_active_users.map((user, index) => (
              <motion.div
                key={user.email}
                className="user-item"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.1 }}
                whileHover={{ x: 4 }}
              >
                <div className="user-rank">{index + 1}</div>
                <div className="user-info">
                  <span className="user-name">{user.username}</span>
                  <span className="user-email">{user.email}</span>
                </div>
                <div className="user-stats">
                  <span className="message-count">{user.message_count} messages</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;